/**
 * SequencePitchMatcher
 *
 * Manages progression through a sequence of notes based on pitch detection.
 * Time-agnostic: only cares about pitch matching, not timing/rhythm.
 *
 * Designed to be composable with future timing validators.
 */

import type { PitchDetectionResult } from './types';
import { noteNameToMidi } from './types';
import { analyzeFrequency } from './noteUtils';

/**
 * Configuration for sequence matching
 */
export interface SequenceMatcherConfig {
  /** Array of note names to match in order (e.g., ["C4", "D4", "E4"]) */
  sequence: string[];

  /**
   * Pitch tolerance in cents (default: 40, max: 45)
   *
   * Maximum is capped at 45 cents to prevent overlap between adjacent semitones.
   * With ±45 cent tolerance, there's a 10-cent dead zone between notes.
   * Recommended default of 40 cents creates a comfortable 20-cent dead zone.
   */
  toleranceCents?: number;

  /**
   * If true, check centsOff in addition to note name.
   * If false, only match note name (very loose - not recommended).
   * Default: true
   */
  strictCents?: boolean;

  /**
   * If true, accept octave errors (e.g., C5 when expecting C4).
   * Default: false
   */
  allowOctaveErrors?: boolean;

  /**
   * How many consecutive frames of silence to ignore before
   * considering a note truly ended.
   * Helps prevent "stuttering" where a faint signal drops for 1-2 frames
   * and causes the matcher to register a new note attack.
   * Default: 5 frames (approx 80ms at 60fps)
   */
  dropoutBufferFrames?: number;

  /**
   * How many consecutive frames of the same note are required before
   * considering it a legitimate new note (not a transient pitch artifact).
   * Helps filter out brief intermediate pitches during note transitions.
   * Default: 5 frames (approx 83ms at 60fps)
   */
  confirmationBufferFrames?: number;
}

/**
 * Current state of sequence matching
 */
export interface SequenceMatcherState {
  /** Index of the note we're currently waiting for */
  currentIndex: number;

  /** Notes that have been successfully matched */
  completedNotes: string[];

  /** Whether the entire sequence has been completed */
  isComplete: boolean;

  /** Progress through sequence (0 to 1) */
  progress: number;

  /** Total number of wrong note attempts (optional tracking) */
  wrongAttempts?: number;
}

/**
 * Events emitted by the matcher
 */
export type SequenceMatchEvent =
  | { type: 'note-matched'; note: string; index: number }
  | { type: 'note-sustained'; note: string; index: number }
  | { type: 'note-ended'; note: string }
  | { type: 'wrong-note'; detected: string; expected: string; index: number; centsOff?: number }
  | { type: 'sequence-complete'; totalAttempts?: number };

/**
 * Manages progression through a sequence of notes.
 *
 * Example:
 * ```typescript
 * const matcher = new SequencePitchMatcher({
 *   sequence: ["C4", "D4", "E4", "F4", "G4"],
 *   toleranceCents: 70,
 *   strictCents: false,
 * });
 *
 * // In pitch detection loop:
 * const event = matcher.processPitch(pitchResult);
 * if (event?.type === 'note-matched') {
 *   console.log(`Matched note ${event.note} at index ${event.index}`);
 * }
 * ```
 */
export class SequencePitchMatcher {
  private config: Required<SequenceMatcherConfig>;
  private state: SequenceMatcherState;
  private lastDetectedNote: string | null = null;
  private consecutiveSilenceFrames: number = 0;
  private pendingNote: string | null = null;
  private consecutiveNoteFrames: number = 0;
  private sequenceCompleted: boolean = false; // Track if sequence is done but waiting for release

  constructor(config: SequenceMatcherConfig) {
    // Validate sequence
    if (!config.sequence || config.sequence.length === 0) {
      throw new Error('Sequence must contain at least one note');
    }

    // Validate and cap tolerance
    const MAX_TOLERANCE_CENTS = 45;
    const DEFAULT_TOLERANCE_CENTS = 40;
    let toleranceCents = config.toleranceCents ?? DEFAULT_TOLERANCE_CENTS;

    if (toleranceCents > MAX_TOLERANCE_CENTS) {
      console.warn(
        `SequencePitchMatcher: toleranceCents=${toleranceCents} exceeds maximum of ${MAX_TOLERANCE_CENTS}. ` +
        `Capping at ${MAX_TOLERANCE_CENTS} to prevent overlap between adjacent semitones. ` +
        `This creates a ${100 - (2 * MAX_TOLERANCE_CENTS)}-cent dead zone between notes.`
      );
      toleranceCents = MAX_TOLERANCE_CENTS;
    }

    // Apply defaults
    this.config = {
      sequence: config.sequence,
      toleranceCents,
      strictCents: config.strictCents ?? true,
      allowOctaveErrors: config.allowOctaveErrors ?? false,
      dropoutBufferFrames: config.dropoutBufferFrames ?? 5,
      confirmationBufferFrames: config.confirmationBufferFrames ?? 5,
    };

    // Initialize state
    this.state = {
      currentIndex: 0,
      completedNotes: [],
      isComplete: false,
      progress: 0,
      wrongAttempts: 0,
    };
  }

  /**
   * Process a pitch detection result.
   * Returns an event if something significant happened, null otherwise.
   */
  processPitch(result: PitchDetectionResult): SequenceMatchEvent | null {
    // Already complete - ignore further input
    if (this.state.isComplete) {
      return null;
    }

    // No pitch detected - handle silence with dropout buffer
    if (!result.frequency || !result.note) {
      this.consecutiveSilenceFrames++;

      // If we are within the grace period, IGNORE the silence.
      // We pretend the last note is still holding.
      if (this.consecutiveSilenceFrames <= this.config.dropoutBufferFrames) {
        return null;
      }

      // Grace period exceeded -> NOW we accept the note has ended
      const wasPlayingNote = this.lastDetectedNote;
      this.lastDetectedNote = null;

      if (wasPlayingNote) {
        // If sequence was completed and we just detected the final note ended,
        // mark as fully complete now
        if (this.sequenceCompleted) {
          this.state.isComplete = true;
        }

        return {
          type: "note-ended",
          note: wasPlayingNote,
        };
      }

      return null;
    }

    // We have a signal, so reset the silence counter immediately
    this.consecutiveSilenceFrames = 0;

    const detectedNote = result.note;

    // Track consecutive frames of the same detected note
    if (detectedNote === this.pendingNote) {
      // Same note as last frame - increment counter
      this.consecutiveNoteFrames++;
    } else {
      // Different note detected - reset confirmation counter
      this.pendingNote = detectedNote;
      this.consecutiveNoteFrames = 1;
    }

    // Check if we've met the confirmation threshold
    const isConfirmedNote = this.consecutiveNoteFrames >= this.config.confirmationBufferFrames;

    // Track whether this is a new note or continued from previous CONFIRMED detection
    const isNewNote = detectedNote !== this.lastDetectedNote;

    // Only update lastDetectedNote once confirmed
    if (isConfirmedNote && isNewNote) {
      this.lastDetectedNote = detectedNote;
    }

    // If sequence is completed, just wait for note to end (don't process matches)
    if (this.sequenceCompleted) {
      // Check if still sustaining the final note
      if (!isNewNote && this.state.currentIndex > 0) {
        const finalNote = this.config.sequence[this.state.currentIndex - 1];
        if (detectedNote === finalNote) {
          return {
            type: 'note-sustained',
            note: detectedNote,
            index: this.state.currentIndex - 1,
          };
        }
      }
      // Ignore any other notes after completion
      return null;
    }

    const expectedNote = this.config.sequence[this.state.currentIndex];

    // Check if still playing the note we just matched (continuous, no gap)
    // Only consider it "sustaining" if it's NOT a new note (continuous from last tick)
    if (!isNewNote && this.state.currentIndex > 0) {
      const previousNote = this.config.sequence[this.state.currentIndex - 1];
      if (detectedNote === previousNote) {
        // Still sustaining the previous matched note (continuous)
        return {
          type: 'note-sustained',
          note: detectedNote,
          index: this.state.currentIndex - 1,
        };
      }
    }

    // Only process matches/wrong-notes once the note is confirmed
    if (!isConfirmedNote) {
      // Still in confirmation phase - ignore this frame
      return null;
    }

    // Check if this matches the expected note
    const isMatch = this.checkNoteMatch(detectedNote, expectedNote, result);

    if (isMatch && isNewNote) {
      // Success! Advance to next note (only on FIRST confirmed detection)
      this.state.completedNotes.push(expectedNote);
      this.state.currentIndex++;
      this.state.progress = this.state.currentIndex / this.config.sequence.length;

      // Check if we just completed the sequence
      if (this.state.currentIndex >= this.config.sequence.length) {
        // Mark sequence as completed, but don't set isComplete yet
        // We need to wait for the final note to be released (note-ended event)
        this.sequenceCompleted = true;
        return {
          type: 'sequence-complete',
          totalAttempts: this.state.wrongAttempts,
        };
      }

      return {
        type: 'note-matched',
        note: expectedNote,
        index: this.state.currentIndex - 1,
      };
    } else if (!isMatch && isNewNote) {
      // Wrong note detected (only count on first confirmed detection, not continuous)
      this.state.wrongAttempts = (this.state.wrongAttempts ?? 0) + 1;

      return {
        type: 'wrong-note',
        detected: detectedNote,
        expected: expectedNote,
        index: this.state.currentIndex,
        centsOff: result.centsOff,
      };
    }

    // Continuing to play same note (already counted as wrong or matched)
    return null;
  }

  /**
   * Check if a detected note matches the expected note
   */
  private checkNoteMatch(
    detectedNote: string,
    expectedNote: string,
    result: PitchDetectionResult
  ): boolean {
    // Convert both notes to MIDI numbers for pitch comparison
    const detectedMidi = noteNameToMidi(detectedNote);
    const expectedMidi = noteNameToMidi(expectedNote);

    // If either conversion failed, fall back to exact string match (shouldn't happen with valid notes)
    if (detectedMidi === null || expectedMidi === null) {
      return detectedNote === expectedNote;
    }

    // Check if MIDI pitches match exactly (handles enharmonic equivalents like A#4 and Bb4)
    if (detectedMidi === expectedMidi) {
      // If strict cents checking is enabled, also check tuning
      if (this.config.strictCents && result.centsOff !== undefined) {
        return Math.abs(result.centsOff) <= this.config.toleranceCents;
      }
      return true;
    }

    // Check for octave errors if allowed (same pitch class, different octave)
    if (this.config.allowOctaveErrors) {
      // Compare pitch classes (MIDI modulo 12) to ignore octave differences
      // This also handles enharmonic equivalents (e.g., A#4 vs Bb4)
      return (detectedMidi % 12) === (expectedMidi % 12);
    }

    return false;
  }

  /**
   * Extract note name without octave (e.g., "C#5" -> "C#")
   */
  private getNoteNameWithoutOctave(note: string): string {
    return note.replace(/\d+$/, '');
  }

  /**
   * Get current state
   */
  getState(): SequenceMatcherState {
    return { ...this.state };
  }

  /**
   * Get the note we're currently waiting for
   */
  getCurrentNote(): string {
    if (this.state.isComplete) {
      return this.config.sequence[this.config.sequence.length - 1];
    }
    return this.config.sequence[this.state.currentIndex];
  }

  /**
   * Get the full sequence
   */
  getSequence(): string[] {
    return [...this.config.sequence];
  }

  /**
   * Reset matcher to beginning
   */
  reset(): void {
    this.state = {
      currentIndex: 0,
      completedNotes: [],
      isComplete: false,
      progress: 0,
      wrongAttempts: 0,
    };
    this.lastDetectedNote = null;
    this.consecutiveSilenceFrames = 0;
    this.pendingNote = null;
    this.consecutiveNoteFrames = 0;
    this.sequenceCompleted = false;
  }

  /**
   * Check if a specific note has been completed
   */
  isNoteCompleted(index: number): boolean {
    return index < this.state.currentIndex;
  }

  /**
   * Check if a specific note is the current target
   */
  isCurrentNote(index: number): boolean {
    return index === this.state.currentIndex && !this.state.isComplete;
  }
}
